import { useState } from "react";
import { Label } from "reactstrap";
import { QRCodeSVG } from "qrcode.react";
import { Check, Clipboard, Download } from "react-bootstrap-icons";

//Common
import FormBackGround from "../common/Forms/FormBackGround/FormBackGround";
import FormHeader from "../common/Forms/FormHeader/FormHeader";
import DisplayData from "../common/DisplayData/DisplayData";
import { BlockIcon, ActivateIcon } from "../common/Icons";
import { Button } from "../common/VFX-UI";
import { vfxTheme } from "../common/VFX-UI/Theme/theme";

//App
import useComponentGeneralStyles from "../styles";
import { EMPTY_GUID } from "../../utility/constants/constants";
import { CampaignViewProps } from "../../types/campaign";
import { getFormattedDate, getFormattedCurrency } from "../../utility/helpers";
import { hasPermission, permissions, IsCauwUser } from "../../utility/helpers/claimHelper";

enum CampaignUrlType {
    Employee = "employee",
    Public = "public",
}

const campaignUrlLabels: Record<CampaignUrlType, string> = {
    [CampaignUrlType.Employee]: "Employee Campaign",
    [CampaignUrlType.Public]: "Public Campaign",
};

const CampaignView = (props: CampaignViewProps) => {
    const {
        onClose,
        editAction,
        toggleActivateHandler,
        toggleDeactivateHandler,
        campaignData,
    } = props;

    const generalStyles = useComponentGeneralStyles();
    const [copiedCampaignUrl, setCopiedCampaignUrl] = useState<CampaignUrlType | null>(null);
    const formatOptionalDate = (date?: string | null) => date ? getFormattedDate(date) : "-";
    const formatDateRange = (startDate?: string | null, endDate?: string | null) => {
        if (!startDate && !endDate) return "-";

        return `${formatOptionalDate(startDate)} - ${formatOptionalDate(endDate)}`;
    };
    const hasCreditCardDates = Boolean(campaignData?.creditCardStartDate || campaignData?.creditCardEndDate);
    const hasCheckDates = Boolean(campaignData?.checkStartDate || campaignData?.checkEndDate);
    const hasPayrollDeductionDates = Boolean(campaignData?.payrollDeductionStartDate || campaignData?.payrollDeductionEndDate);
    const hasPaymentTypeDates = hasCreditCardDates || hasCheckDates || hasPayrollDeductionDates;

    const copyCampaignUrl = async (urlType: CampaignUrlType, url: string) => {
        await navigator.clipboard.writeText(url);
        setCopiedCampaignUrl(urlType);
        window.setTimeout(() => setCopiedCampaignUrl(null), 2000);
    };

    const downloadCampaignQrCode = (urlType: CampaignUrlType, label: string) => {
        const qrCode = document.getElementById(`${urlType}-campaign-qr-code`);

        if (!qrCode) return;

        const svg = new XMLSerializer().serializeToString(qrCode);
        const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(blob);
        const image = new Image();

        image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = image.width;
            canvas.height = image.height;

            const context = canvas.getContext("2d");
            if (!context) {
                URL.revokeObjectURL(svgUrl);
                return;
            }

            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0);

            canvas.toBlob((pngBlob) => {
                if (!pngBlob) return;

                const downloadUrl = URL.createObjectURL(pngBlob);
                const link = document.createElement("a");

                link.href = downloadUrl;
                link.download = `${label.toLowerCase().replace(/\s+/g, "-")}-qr-code.png`;
                link.click();
                URL.revokeObjectURL(downloadUrl);
            }, "image/png");

            URL.revokeObjectURL(svgUrl);
        };

        image.onerror = () => URL.revokeObjectURL(svgUrl);
        image.src = svgUrl;
    };

    const renderCampaignQrCode = (
        urlType: CampaignUrlType,
        url: string
    ) => {
        const isCopied = copiedCampaignUrl === urlType;
        const label = campaignUrlLabels[urlType];

        return (
            <div>
                <div
                    className="card h-100 w-100 p-3 d-flex flex-column align-items-center gap-3 mx-auto"
                    style={{ maxWidth: 260 }}
                >
                    <strong
                        className="text-center d-flex align-items-center justify-content-center"
                        style={{ minHeight: 48 }}
                    >
                        {label}
                    </strong>
                    <QRCodeSVG
                        id={`${urlType}-campaign-qr-code`}
                        value={url}
                        size={150}
                        title={`${label} QR Code`}
                        style={{ maxWidth: "100%", height: "auto" }}
                    />
                    <div className="d-grid gap-2 w-100 mt-auto">
                    <button
                        type="button"
                            className="btn btn-outline-primary"
                        title={url}
                        onClick={() => copyCampaignUrl(urlType, url)}
                    >
                        {isCopied ? <Check size={18} /> : <Clipboard size={18} />}
                            <span className="ms-2">{isCopied ? "Copied" : "Copy link"}</span>
                    </button>
                        <Button
                            style={{
                                backgroundColor: vfxTheme.palette.primary.main,
                                borderColor: vfxTheme.palette.primary.main,
                                width: "100%",
                            }}
                            onClick={() => downloadCampaignQrCode(urlType, label)}
                        >
                            <Download size={18} />
                            <span className="ms-2">Download QR</span>
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <FormBackGround>
            <FormHeader
                title={campaignData?.name}
                subTitle={campaignData?.description}
                chipLabel={campaignData?.isCompleted ? "Completed" : campaignData?.isActive ? campaignData?.status! : "Inactive"}
                onClose={onClose}
                editActionHandler={hasPermission([permissions.MANAGE_CAMPAIGNS]) && campaignData?.isActive ? editAction : undefined}
            />
            <>
                {IsCauwUser() && <DisplayData text="Organization" importedData={campaignData?.organization}></DisplayData>}
                
                <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2">Information</Label>
                <div className="ms-3">
                    <DisplayData text="Code" importedData={campaignData?.code} />
                    <DisplayData text="Campaign Duration" importedData={formatDateRange(campaignData?.startDate, campaignData?.endDate)} />
                </div>

                <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2">Pledge Details</Label>
                <div className="ms-3">
                    <DisplayData text="Pledge Goal" importedData={getFormattedCurrency(campaignData?.pledgeGoal)} />
                    <DisplayData text="Payroll Deduction Start Date" importedData={getFormattedDate(campaignData?.donationStartDate)} />
                </div>

                {hasPaymentTypeDates && (
                    <>
                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2">Payment Types</Label>
                        <div className="ms-3">
                            {hasCreditCardDates && (
                                <DisplayData text="Credit Card" importedData={formatDateRange(campaignData?.creditCardStartDate, campaignData?.creditCardEndDate)} />
                            )}
                            {hasCheckDates && (
                                <DisplayData text="Check" importedData={formatDateRange(campaignData?.checkStartDate, campaignData?.checkEndDate)} />
                            )}
                            {hasPayrollDeductionDates && (
                                <DisplayData text="Payroll Deduction" importedData={formatDateRange(campaignData?.payrollDeductionStartDate, campaignData?.payrollDeductionEndDate)} />
                            )}
                        </div>
                    </>
                )}

                {!campaignData?.isCompleted &&
                    (campaignData?.employeeCampaignUrl || campaignData?.publicCampaignUrl) && (
                    <>
                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2">Campaign QR Codes</Label>
                        <div
                            className="d-grid gap-4 mb-3"
                            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 260px))" }}
                        >
                            {campaignData.employeeCampaignUrl &&
                                renderCampaignQrCode(
                                    CampaignUrlType.Employee,
                                    campaignData.employeeCampaignUrl
                                )}
                            {campaignData.publicCampaignUrl &&
                                renderCampaignQrCode(
                                    CampaignUrlType.Public,
                                    campaignData.publicCampaignUrl
                                )}
                        </div>
                    </>
                )}
            </>
            <div className="border-top container-fluid p-3 mt-auto">
                {hasPermission([permissions.MANAGE_CAMPAIGNS]) && campaignData?.id && campaignData?.id !== EMPTY_GUID && !campaignData?.isCompleted && campaignData?.isActive && campaignData?.status?.toLowerCase() !== 'active' && (
                    <Button style={generalStyles.deleteButton} onClick={toggleDeactivateHandler} >
                        <BlockIcon />
                    </Button>

                )}
                {hasPermission([permissions.MANAGE_CAMPAIGNS]) && campaignData?.id && campaignData?.id !== EMPTY_GUID && !campaignData?.isCompleted && !campaignData?.isActive && (
                    <Button style={generalStyles.activateButton} onClick={toggleActivateHandler} >
                        <ActivateIcon />
                    </Button>
                )}
            </div>
        </FormBackGround>
    );
};

export default CampaignView;
